import { UserEventContext } from "api/userEvents";

import { IScreenshotSettings } from "models/screenshot";

import { WidgetContextExtractor } from "components/screenshot/utils/widgetContextExtractors";

interface XDAWidgetSettings {
  showIpdb?: boolean;
  showIpdbLegend?: boolean;
  ipdbSource?: string;
  ipdbField?: string;
  topsModelSource?: string;
  isUsingResourcePlayDefault?: boolean;
  selectedRadius?: number;
  dataFields?: string[];
  widthScaled?: boolean;
  showOverlap?: boolean;
  showDucWells?: boolean;
  showGrid?: boolean;
  showMeasurement?: boolean;
  showRelativeDepth?: boolean;
  showTVDSSDepth?: boolean;
  showAllTops?: boolean;
  hangWellsToTop?: boolean;
}

interface XDAWidgetState {
  settings?: XDAWidgetSettings;
}

/**
 * XDA Viewer-specific context extractor for screenshot events
 * Extracts relevant XDA information like 3D geo settings, selected properties, etc.
 */
export class XDAContextExtractor implements WidgetContextExtractor {
  widgetType = "xdaViewer";

  extractContext(
    widgetState: XDAWidgetState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _settings: IScreenshotSettings
  ): UserEventContext {
    const context: UserEventContext = {};

    if (!widgetState?.settings) {
      return context;
    }

    // Extract IPDB-related settings
    this.extractIpdbContext(widgetState.settings, context);

    // Extract model and play settings
    this.extractModelContext(widgetState.settings, context);

    // Extract visualization settings
    this.extractVisualizationContext(widgetState.settings, context);

    // Extract depth and measurement settings
    this.extractDepthMeasurementContext(widgetState.settings, context);

    return context;
  }

  private extractIpdbContext(
    settings: XDAWidgetSettings,
    context: UserEventContext
  ): void {
    context.showIpdb = settings.showIpdb;

    if (settings.showIpdb) {
      context.showIpdbLegend = settings.showIpdbLegend;
      context.ipdbSource = settings.ipdbSource;
      context.ipdbField = settings.ipdbField;
    }
  }

  private extractModelContext(
    settings: XDAWidgetSettings,
    context: UserEventContext
  ): void {
    context.topsModelSource = settings.topsModelSource;
    context.isUsingResourcePlayDefault = settings.isUsingResourcePlayDefault;

    if (settings.isUsingResourcePlayDefault) {
      context.selectedRadius = settings.selectedRadius;
    }

    context.dataFields = settings.dataFields;
  }

  private extractVisualizationContext(
    settings: XDAWidgetSettings,
    context: UserEventContext
  ): void {
    context.widthScaled = settings.widthScaled;
    context.showOverlap = settings.showOverlap;
    context.showDucWells = settings.showDucWells;
    context.showGrid = settings.showGrid;
    context.showAllTops = settings.showAllTops;
    context.hangWellsToTop = settings.hangWellsToTop;
  }

  private extractDepthMeasurementContext(
    settings: XDAWidgetSettings,
    context: UserEventContext
  ): void {
    context.showMeasurement = settings.showMeasurement;
    context.showRelativeDepth = settings.showRelativeDepth;
    context.showTVDSSDepth = settings.showTVDSSDepth;
  }
}
